import { type FC, Suspense, useEffect } from "react";
import { Outlet, ScrollRestoration } from "react-router-dom";

import { MantineProvider } from "@mantine/core";
import { printWarning } from "@snapchat/self-xss-warning";
import { ConfigProvider, Notification } from "@snapchat/snap-design-system";
import enUS from "antd/es/locale/en_US";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { IntlProvider } from "react-intl";
import { RecoilRoot } from "recoil";
import "@mantine/core/styles.css";

import { AnalyticsContainer } from "./analytics/components";
import { AppApolloProvider } from "./apollo/provider";
import { GroupListPaginationProvider } from "./camkit/groups/context";
import { LensListPaginationProvider } from "./camkit/lenses/context";
import { CofConfigsContainer } from "./core/development/components";
import { RecoilDebugObserver } from "./core/recoil";
import { AppRoutes } from "./core/routes/AppRoutes";
import { DevToolsGuard } from "./dev-tools";
import { ErrorBoundary, ProjectId } from "./error-boundary";
import { AppContainer, AppSSOAuthProvider, AuthGuard } from "./layout/components";
import { Sentry } from "./sentry";
import { isProdLike } from "./shared/constants";

export const App: FC = () => {
    useEffect(() => {
        if (isProdLike()) {
            printWarning();
        }
    }, []);

    return (
        <Sentry>
            <ErrorBoundary projectId={ProjectId.MY_LENSES}>
                <MantineProvider>
                    <AppSSOAuthProvider>
                        <AuthGuard>
                            <Notification.Provider>
                                <AppApolloProvider>
                                    <RecoilRoot>
                                        <DevToolsGuard>
                                            <RecoilDebugObserver />
                                            <CofConfigsContainer />
                                        </DevToolsGuard>
                                        <Suspense fallback={null}>
                                            <ConfigProvider locale={enUS}>
                                                <IntlProvider locale={enUS.locale}>
                                                    <DndProvider backend={HTML5Backend}>
                                                        <AppContainer>
                                                            <AnalyticsContainer>
                                                                <GroupListPaginationProvider>
                                                                    <LensListPaginationProvider>
                                                                        <ScrollRestoration />
                                                                        <AppRoutes>
                                                                            <Outlet />
                                                                        </AppRoutes>
                                                                    </LensListPaginationProvider>
                                                                </GroupListPaginationProvider>
                                                            </AnalyticsContainer>
                                                        </AppContainer>
                                                    </DndProvider>
                                                </IntlProvider>
                                            </ConfigProvider>
                                        </Suspense>
                                    </RecoilRoot>
                                </AppApolloProvider>
                            </Notification.Provider>
                        </AuthGuard>
                    </AppSSOAuthProvider>
                </MantineProvider>
            </ErrorBoundary>
        </Sentry>
    );
};
